<script setup>
import { computed, ref, watch } from 'vue'

// Component props
const componentProps = defineProps({
  options: { type: Array, required: true }, // string[] | number[]
  modelValue: { type: [Array, null], required: true }, // string[] | number[] | null
  valueType: {
    type: String,
    required: false,
    default: 'string',
    validator: (v) => ['string', 'number'].includes(v) || v === '',
  },
})

const emit = defineEmits(['update:modelValue'])

// Refs
const inputValue = ref('')
const labelSelector = ref()

const normalizeModelItem = (v) => {
  if (componentProps.valueType === 'number') {
    if (typeof v === 'number' && Number.isFinite(v)) return v
    const n = Number(String(v).replace(',', '.'))
    return Number.isFinite(n) ? n : null
  }
  return String(v)
}

const equalsModel = (a, b) => {
  if (componentProps.valueType === 'number') return a === b
  return String(a).toLowerCase() === String(b).toLowerCase()
}

const modelArray = computed(() => componentProps.modelValue || [])

const modelNormalized = computed(() => {
  const out = []
  for (const item of modelArray.value) {
    const n = normalizeModelItem(item)
    if (n === null) continue
    if (!out.some((x) => equalsModel(x, n))) out.push(n)
  }
  return out
})

const optionsNormalized = computed(() => {
  const out = []
  for (const opt of componentProps.options || []) {
    const n = normalizeModelItem(opt)
    if (n === null) continue
    if (!out.some((x) => equalsModel(x, n))) out.push(n)
  }
  return out
})

const toOptionObject = (val) => ({
  value: val,
  label: componentProps.valueType === 'number' ? String(val) : String(val),
})

const sortOptionObjects = (arr) => {
  const copy = [...arr]
  if (componentProps.valueType === 'number') {
    copy.sort((a, b) => Number(a.value) - Number(b.value))
  } else {
    copy.sort((a, b) => a.label.localeCompare(b.label))
  }
  return copy
}

const availableOptionObjects = computed(() => {
  const available = optionsNormalized.value.filter((opt) => !modelNormalized.value.some((m) => equalsModel(m, opt)))
  return sortOptionObjects(available.map(toOptionObject))
})

const optionsModel = ref(availableOptionObjects.value)
const filteredOptions = ref([...optionsModel.value])

watch(
  () => [componentProps.options, componentProps.modelValue, componentProps.valueType],
  () => {
    optionsModel.value = availableOptionObjects.value
    filteredOptions.value = [...optionsModel.value]
  },
  { deep: false },
)

const showNewItem = computed(() => {
  if (!inputValue.value) return false
  const candidate = normalizeModelItem(inputValue.value)
  if (candidate === null) return false
  if (modelNormalized.value.some((m) => equalsModel(m, candidate))) return false

  // already in current filtered options?
  return !filteredOptions.value.some((o) => equalsModel(o.value, candidate))
})

// Methods
const filterFn = (val, update, abort) => {
  if (filteredOptions.value.length === 0 && !showNewItem.value) {
    abort()
    return
  }

  if (val === '') {
    update(() => {
      filteredOptions.value = [...optionsModel.value]
    })
    return
  }

  update(() => {
    const needle = String(val).toLowerCase()

    filteredOptions.value = optionsModel.value.filter((o) => o.label.toLowerCase().includes(needle))

    filteredOptions.value = sortOptionObjects(filteredOptions.value)

    if (showNewItem.value) {
      const candidate = normalizeModelItem(val)
      if (candidate !== null && !filteredOptions.value.some((o) => equalsModel(o.value, candidate))) {
        filteredOptions.value.unshift(toOptionObject(candidate))
      }
    }
  })
}

const sanitizeNumericInput = (val) => {
  // digits only (optionally allow 1 dot)
  const cleaned = String(val)
    .replace(/\s+/g, '')
    .replace(/,/g, '.')
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*)\./g, '$1')

  return cleaned
}

const onInputValue = (val) => {
  if (componentProps.valueType !== 'number') {
    inputValue.value = val
    return
  }

  const cleaned = sanitizeNumericInput(val)
  inputValue.value = cleaned

  // force q-select's visible input to match
  if (labelSelector.value?.updateInputValue) {
    labelSelector.value.updateInputValue(cleaned)
  }
}

const onKeyDown = (e) => {
  if (componentProps.valueType !== 'number') return

  // Let composition/IME do its thing; sanitizing will handle final value
  if (e.isComposing) return

  const allowed = new Set(['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Escape', 'Home', 'End'])

  if (allowed.has(e.key)) return

  // Allow shortcuts (copy/paste/select all/undo/redo)
  if (e.ctrlKey || e.metaKey) return

  // digits
  if (/^[0-9]$/.test(e.key)) return

  // one decimal separator
  if ((e.key === '.' || e.key === ',') && !String(inputValue.value).includes('.')) return

  e.preventDefault()
}

// Extra fail-safe for paste (context menu paste often bypasses keydown logic)
const onPaste = (e) => {
  if (componentProps.valueType !== 'number') return

  const text = e.clipboardData?.getData('text') ?? ''
  const cleaned = sanitizeNumericInput(text)

  e.preventDefault()

  // Insert at cursor position if possible, else just replace
  const target = e.target
  const start = target?.selectionStart ?? 0
  const end = target?.selectionEnd ?? 0

  const current = String(inputValue.value || '')
  const next = current.slice(0, start) + cleaned + current.slice(end)

  onInputValue(next)
}

// Catch drag/drop text into the input
const onDrop = (e) => {
  if (componentProps.valueType !== 'number') return

  const text = e.dataTransfer?.getData('text') ?? ''
  const cleaned = sanitizeNumericInput(text)

  e.preventDefault()
  onInputValue(cleaned)
}

const emitValue = (value) => {
  const arr = Array.isArray(value) ? value : []

  const normalized = []
  for (const item of arr) {
    const n = normalizeModelItem(item)
    if (n === null) continue
    if (!normalized.some((x) => equalsModel(x, n))) normalized.push(n)
  }

  emit('update:modelValue', normalized)

  // refresh options based on what we just emitted
  optionsModel.value = availableOptionObjects.value
  filteredOptions.value = [...optionsModel.value]

  clearInput()
}

const addOption = (optOrVal) => {
  if (!optOrVal) return

  const raw = typeof optOrVal === 'object' ? optOrVal.value : optOrVal
  const candidate = normalizeModelItem(raw)
  if (candidate === null) {
    clearInput()
    return
  }

  if (!modelNormalized.value.some((m) => equalsModel(m, candidate))) {
    emitValue([...modelNormalized.value, candidate])
  } else {
    clearInput()
  }
}

const onNewValue = (val, done) => {
  const candidate = normalizeModelItem(val)
  if (candidate === null) {
    done(null)
    clearInput()
    return
  }

  if (modelNormalized.value.some((m) => equalsModel(m, candidate))) {
    done(null)
    clearInput()
    return
  }

  // If it exists in original options, keep canonical value (esp. strings with different casing)
  const existing = optionsNormalized.value.find((o) => equalsModel(o, candidate))
  const finalValue = existing !== undefined ? existing : candidate

  // Important: done() receives the option-object so QSelect can add it correctly
  const optionObj = toOptionObject(finalValue)
  done(optionObj, 'add-unique')
  emitValue([...modelNormalized.value, finalValue])
}

const onRemove = (removedOption) => {
  const removed = normalizeModelItem(removedOption.value)
  if (removed === null) return
  emitValue(modelNormalized.value.filter((x) => !equalsModel(x, removed)))
}

const clearInput = () => {
  labelSelector.value?.updateInputValue('')
  labelSelector.value?.hidePopup()
  setTimeout(() => {
    labelSelector.value?.hidePopup()
    labelSelector.value?.focus()
  }, 1)
}
</script>

<template>
  <div>
    <q-select
      ref="labelSelector"
      :model-value="modelValue"
      :options="filteredOptions"
      option-label="label"
      option-value="value"
      emit-value
      map-options
      outlined
      use-input
      @filter="filterFn"
      @input-value="onInputValue"
      @keydown="onKeyDown"
      @paste="onPaste"
      @drop="onDrop"
      new-value-mode="add-unique"
      @new-value="onNewValue"
      @remove="onRemove"
      @keyup.enter.stop="addOption($event.target.value)"
      multiple
      v-bind="$attrs"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>

      <template #option="{ opt }">
        <q-item clickable @click="addOption(opt)">
          <q-item-section>
            <div class="text-primary flex flex-inline">
              <q-icon
                name="add"
                v-if="showNewItem && inputValue && normalizeModelItem(inputValue) !== null && String(opt.value) === String(normalizeModelItem(inputValue))"
              />
              {{ opt.label }}
              <span v-if="showNewItem && inputValue && normalizeModelItem(inputValue) !== null && String(opt.value) === String(normalizeModelItem(inputValue))">
                ({{ $t('Shared.Globals.New label') }})
              </span>
            </div>
          </q-item-section>
        </q-item>
      </template>
    </q-select>
  </div>
</template>
